import type { ListUsersInput } from "@nexxt/contracts/party";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { partyClient } from "../client";
import { partyQueries } from "../party/party.queries";

// Server state of the users (50-api/party.md §2.1): the list by page, one user, and the roles the forms offer. What
// only Tomorrow reads of a user (its history, how it signs in) the app keeps under `details`.

/** The filters of the list input (`listUsersInput`), without the paging the query adds. */
export type UserFilters = Pick<ListUsersInput, "q" | "orgType" | "status" | "role"> & {
  /** The sets of the queues "To do", as the app writes them (ADR-302). */
  anyOf?: string | undefined;
};

const PAGE_SIZE = 50;

const keys = {
  all: [...partyQueries.all, "users"] as const,
  lists: [...partyQueries.all, "users", "list"] as const,
  details: [...partyQueries.all, "users", "detail"] as const,
};

export const userQueries = {
  ...keys,

  list: (filters: UserFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        partyClient().listUsers({ ...filters, page: pageParam, pageSize: PAGE_SIZE }, signal),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  detail: (userId: string) =>
    queryOptions({
      queryKey: [...keys.details, userId],
      queryFn: ({ signal }) => partyClient().getUser({ userId }, signal),
    }),

  /**
   * The roles a user can be given, with who each is for (ADR-048): to Tomorrow every role; to an organization's own
   * administrator, only those its organization gives (ADR-172).
   */
  options: () =>
    queryOptions({
      queryKey: [...keys.all, "options"],
      queryFn: ({ signal }) => partyClient().getUserOptions({}, signal),
      staleTime: 10 * 60_000,
    }),
};
